/**
 * Analytics utilities for tracking user interactions and loan applications
 */
import { LoanCycle } from "../helpers/loan_cycle_helper";
import { ANALYTICS_EVENTS } from "../helpers/analytics_events_helper";
import { ProductType } from "../types/product";

// Track the last time each event was fired
const eventTimestamps = new Map<string, number>();
const EVENT_DEBOUNCE_MS = 1000; // 1 second debounce window

// Custom dimensions for analysis/segmentation
export interface CustomDimensions {
  product_type?: ProductType;
  product_amount?: number;
  product_length?: number;
  product_apr?: number;
  product_state?: string;
  application_number?: number;
  has_open_loan?: boolean;
  is_logged_in?: boolean;
  funded_count?: number;
  entry_point?: string;
  flow_context?: string;
  validation_errors?: string[];
  error_message?: string;

  // Experiment tracking dimensions
  experiment_id?: string;
  experiment_variant?: string;
  experiment_content?: string;
  is_override?: boolean;
}

// Event metadata for correlation
export interface EventMetadata {
  user_id?: string;
  active_loan_id?: string;
  session_id?: string;
}

export interface UserContext {
  applicationNumber?: number;
  activeLoanCycle?: string;
  fundedCount?: number;
}

export interface ProductContext {
  product?: ProductType;
  amount?: number;
  length?: number;
  apr?: number;
  state?: string;
}

export interface AnalyticsContext {
  entry_point?: string;
  flow_context?: string;
}


export interface ErrorContext {
  validation_errors?: string[];
  error_message?: string | string[];
}

export interface ExperimentTrackingData {
  experimentId: string;
  experimentVariant: string;
  experimentContent: string;
  isOverride: boolean;
}

// Utility functions
const isAnalyticsEnabled = (): boolean => {
  return (
    process.env.REACT_APP_ANALYTICS_ENABLED === "true" &&
    typeof window !== "undefined"
  );
};

const hasDataLayer = (win: Window): win is Window & { dataLayer: any[] } => {
  return Array.isArray(win.dataLayer);
};

const logEvent = (eventName: string, data: any): void => {
  if (
    process.env.NODE_ENV === "development" ||
    process.env.NODE_ENV === "test"
  ) {
    console.log("📊", eventName, data);
  }
};

const validateEventName = (eventName: string): boolean => {
  const trimmedEventName = eventName && eventName.trim();
  return (
    typeof trimmedEventName === "string" &&
    trimmedEventName.length > 0 &&
    Object.values(ANALYTICS_EVENTS).includes(
      trimmedEventName as keyof typeof ANALYTICS_EVENTS
    )
  );
};

// Core tracking function
export const trackEvent = (
  eventName: string,
  customDimensions: CustomDimensions = {},
  metadata: EventMetadata = {}
): void => {
  if (!validateEventName(eventName)) {
    return;
  }

  const now = Date.now();
  const lastFired = eventTimestamps.get(eventName);
  if (lastFired && (now - lastFired) <= EVENT_DEBOUNCE_MS) {
    if (process.env.NODE_ENV === "development") {
      console.log("📊 Debounced event:", eventName);
    }
    return;
  }
  eventTimestamps.set(eventName, now);

  const eventData = {
    event: eventName.trim(),
    // Event parameters for correlation
    user_id: metadata.user_id,
    active_loan_id: metadata.active_loan_id,
    session_id: metadata.session_id,
    // Custom dimensions for analysis
    ...customDimensions,
  };

  // Always log in development/test mode
  logEvent(eventName, eventData);

  // Only push to dataLayer if analytics is enabled
  if (isAnalyticsEnabled() && hasDataLayer(window)) {
    try {
      window.dataLayer.push(eventData);
    } catch (error) {
      console.error("Analytics error:", error);
    }
  }
};

// Product context extraction
const extractProductContext = (product: ProductContext = {}) => ({
  product_type:
    product.product && Object.values(ProductType).includes(product.product as ProductType)
      ? (product.product as ProductType)
      : undefined,
  product_amount:
    typeof product.amount === "number" ? product.amount : undefined,
  product_length:
    typeof product.length === "number" ? product.length : undefined,
  product_apr: typeof product.apr === "number" ? product.apr : undefined,
  product_state: typeof product.state === "string" ? product.state : undefined,
});

// User context extraction
const extractUserContext = (user: UserContext = {}) => {
  const hasUser = user && Object.keys(user).length > 0;

  return {
    is_logged_in: hasUser,
    application_number:
      hasUser && typeof user.applicationNumber === "number"
        ? user.applicationNumber + 1
        : undefined,
    has_open_loan:
      hasUser && user.activeLoanCycle
        ? LoanCycle.ACTIVE.includes(user.activeLoanCycle)
        : undefined,
    funded_count:
      hasUser && typeof user.fundedCount === "number"
        ? user.fundedCount
        : undefined,
  };
};

// Analytics context extraction
export const extractAnalyticsContext = (
  analyticsContext: AnalyticsContext = {}
): AnalyticsContext => ({
  entry_point:
    typeof analyticsContext.entry_point === "string"
      ? analyticsContext.entry_point.trim()
      : undefined,
  flow_context:
    typeof analyticsContext.flow_context === "string"
      ? analyticsContext.flow_context.trim()
      : undefined,
});

// Error context extraction
const extractErrorContext = (errorContext: ErrorContext = {}): { validation_errors?: string[]; error_message?: string } => ({
  validation_errors:
    Array.isArray(errorContext.validation_errors) && errorContext.validation_errors.length > 0
      ? errorContext.validation_errors
      : undefined,
  error_message: (() => {
    if (typeof errorContext.error_message === 'string') {
      const trimmed = errorContext.error_message.trim();
      return trimmed.length > 0 ? trimmed : undefined;
    } else if (Array.isArray(errorContext.error_message) && errorContext.error_message.length > 0) {
      const firstMessage = errorContext.error_message[0];
      if (typeof firstMessage === 'string') {
        const trimmed = firstMessage.trim();
        return trimmed.length > 0 ? trimmed : undefined;
      }
    }
    return undefined;
  })(),
});

// Application event tracking
export const trackApplicationEvent = (
  eventName: string,
  productContext: ProductContext,
  userContext: UserContext,
  analyticsContext: AnalyticsContext,
  ErrorContext: ErrorContext = {}
): void => {
  if (!validateEventName(eventName)) {
    return;
  }

  // Extract standard contexts
  const extractedProductContext = extractProductContext(productContext);
  const extractedUserContext = extractUserContext(userContext);
  const extractedAnalyticsContext = extractAnalyticsContext(analyticsContext);
  const extractedErrorContext = extractErrorContext(ErrorContext);

  trackEvent(eventName, {
    ...extractedProductContext,
    ...extractedUserContext,
    ...extractedAnalyticsContext,
    ...extractedErrorContext,
  });
};

// Experiment tracking
export const trackExperimentImpression = (data: ExperimentTrackingData, metadata?: EventMetadata): void => {
  trackEvent(ANALYTICS_EVENTS.AB_TEST_ASSIGNMENT, {
    experiment_id: data.experimentId,
    experiment_variant: data.experimentVariant,
    experiment_content: data.experimentContent,
    is_override: data.isOverride
  }, metadata);
};
